import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { routes } from '@platform/core/lib/routes';
import type { IAdvancedCondition } from '@platform/engine/list/advanced/IAdvancedCondition';
import { advancedKey, conditionsStore } from '@platform/engine/list/advanced/advancedStore';
import { useLabel } from '@platform/hooks/useLabel';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { useGetCatalogQuery } from '@platform/state/api';
import { ErrorBanner, Spinner } from '@platform/ui';
import { BaCoverageHead } from './BaCoverageHead';
import { BaKindTiles } from './BaKindTiles';
import { BaModuleMatrix } from './BaModuleMatrix';
import { BaStakeholders } from './BaStakeholders';
import type { IBaCoverageProps } from './IBaCoverageProps';
import { useBaCoverage } from './useBaCoverage';
import './ba.css';

const is = (field: string, value: string): IAdvancedCondition => ({ field, op: 'eq', value });

/**
 * «Покриття аналізом» — «Аналітика» модуля «Бізнес-аналіз» і його подання «Матриця покриття» (прототип v4 isBaDash): шапка з підсумком і
 * перемикачем рядків, тайли видів, «Модулі × аналіз», «Зацікавлені сторони × аналіз». Клік відкриває таблицю аналізу з розширеними
 * фільтрами виду, модуля чи сторони (як navGo прототипу з фільтром «f:kind=…&module=…»).
 */
export function BaCoverage({ model }: IBaCoverageProps) {
  const { t } = useTranslation();
  const label = useLabel();
  const navigate = useNavigate();
  const strings = useModuleStrings();
  const [gapsOnly, setGapsOnly] = useState(false);
  const { coverage, kinds, error } = useBaCoverage(model);
  const { data: catalog } = useGetCatalogQuery({ workspaceId: model.ws });
  if (error) return <ErrorBanner error={error} />;
  if (!coverage) return <Spinner />;
  const rt = model.rt;
  const open = (conditions: IAdvancedCondition[]) => {
    conditionsStore.write(advancedKey(model.ws, rt.key), conditions);
    navigate(routes.list(model.module.module.key, rt.key, 'table'));
  };
  const option = (field: string) => (value: string) =>
    strings.option(rt.key, field, value, rt.fields.find((f) => f.key === field)?.options?.find((o) => o.value === value)?.label ?? value);
  // Категорія — словником прототипу за англійським ключем («Управління відносинами», «Правові»), інакше назвою каталогу.
  const categoryOf = (category: string) => (category === '@platform' ? t('modBody.ba.wholePlatform') : category === '@system' ? t('modBody.ba.system')
    : t(`modBody.ba.categories.${category}`, { defaultValue: label(catalog?.categories.find((c) => c.key === category)?.name) || category }));
  return (
    <div className="ba">
      <BaCoverageHead coverage={coverage} gapsOnly={gapsOnly} onMode={setGapsOnly} />
      <BaKindTiles kinds={kinds} byKind={coverage.byKind} onOpen={(kind) => open([is('kind', kind)])} />
      <span className="ba-label">{t('modBody.ba.matrix')}</span>
      <BaModuleMatrix rows={gapsOnly ? coverage.rows.filter((r) => r.gaps) : coverage.rows} nameOf={option('module')} categoryOf={categoryOf}
        onOpenModule={(value) => open([is('module', value)])} onOpenCell={(kind, value) => open([is('kind', kind), is('module', value)])} />
      <span className="ba-label">{t('modBody.ba.stakeholders')}</span>
      <BaStakeholders rows={coverage.stakes} nameOf={option('stakeholder')} onOpen={(kind, value) => open([is('kind', kind), is('stakeholder', value)])} />
    </div>
  );
}
